import { useAssistantState } from '@assistant-ui/react';

import {
  AIChatDataTypes,
  AIChatStatuses,
  AIChatToolIds,
  AIChatToolTypes,
  AssetDraftFromPhotoWorkflowStep,
} from '@atom/types/ai';

const ANALYTICS_STATUSES = [
  AIChatStatuses.ANALYZING_QUESTION,
  AIChatStatuses.CONSTRUCTING_QUERY,
  AIChatStatuses.QUERYING,
  AIChatStatuses.ANALYZING_RESULTS,
];

const ASSET_DRAFT_WORKFLOW_STEP_STATUS: Partial<
  Record<AssetDraftFromPhotoWorkflowStep, AIChatStatuses>
> = {
  [AssetDraftFromPhotoWorkflowStep.EXTRACT_PHOTO]:
    AIChatStatuses.EXTRACTING_PHOTO,
  [AssetDraftFromPhotoWorkflowStep.PARSE_EXIF]: AIChatStatuses.EXTRACTING_PHOTO,
  [AssetDraftFromPhotoWorkflowStep.FETCH_SCHEMAS]:
    AIChatStatuses.FINDING_SCHEMA,
  [AssetDraftFromPhotoWorkflowStep.GEOCODE_ADDRESS]:
    AIChatStatuses.FINDING_SCHEMA,
  [AssetDraftFromPhotoWorkflowStep.MATCH_SCHEMA]: AIChatStatuses.FINDING_SCHEMA,
  [AssetDraftFromPhotoWorkflowStep.FETCH_SCHEMA_DETAIL]:
    AIChatStatuses.FINDING_CATEGORY,
  [AssetDraftFromPhotoWorkflowStep.FETCH_ASSET_PATHS]:
    AIChatStatuses.FINDING_CATEGORY,
  [AssetDraftFromPhotoWorkflowStep.MATCH_CATEGORY]:
    AIChatStatuses.FINDING_CATEGORY,
  [AssetDraftFromPhotoWorkflowStep.FILL_ATTRIBUTES]:
    AIChatStatuses.FILLING_ATTRIBUTES,
  [AssetDraftFromPhotoWorkflowStep.BUILD_RESULT]:
    AIChatStatuses.PREPARING_DRAFT,
};

const IGNORED_TOOLS: string[] = [AIChatToolIds.UPDATE_WORKING_MEMORY];

const getStepStatus = (name: string): AIChatStatuses | undefined =>
  ASSET_DRAFT_WORKFLOW_STEP_STATUS[name as AssetDraftFromPhotoWorkflowStep];

const isTrackedToolCall = (part: any): boolean =>
  part?.type === 'tool-call' && !IGNORED_TOOLS.includes(part.toolName);

const getWorkflowStatus = (parts: readonly any[]): AIChatStatuses | null => {
  const workflowPart = [...parts]
    .reverse()
    .find((part: any) => part.type === 'data' && part.name === 'tool-workflow');

  if (!workflowPart) {
    return null;
  }

  const steps = ((workflowPart as any).data?.steps ?? {}) as Record<
    string,
    { status: string }
  >;

  const entries = Object.entries(steps);

  const running = entries.findLast(
    ([name, step]) => step.status === 'running' && !!getStepStatus(name),
  );

  if (running) {
    return getStepStatus(running[0]) ?? null;
  }

  const last = entries.findLast(([name]) => !!getStepStatus(name));

  return last ? (getStepStatus(last[0]) ?? null) : null;
};

const getAnalyticsStatus = (parts: readonly any[]): AIChatStatuses | null => {
  const lastToolIndex = parts.findLastIndex(isTrackedToolCall);

  const partsSinceTool =
    lastToolIndex >= 0 ? parts.slice(lastToolIndex + 1) : parts;

  const statusCount = partsSinceTool.filter(
    (part: any) =>
      part.type === 'data' &&
      part.name === AIChatDataTypes.CONVERSATIONAL_ANALYTICS_STATUS,
  ).length;

  const index = Math.min(statusCount - 1, ANALYTICS_STATUSES.length - 1);

  return index >= 0 ? ANALYTICS_STATUSES[index] : null;
};

const getToolCallStatus = (parts: readonly any[]): AIChatStatuses | null => {
  const lastTool = [...parts].reverse().find(isTrackedToolCall);

  if (!lastTool) {
    return null;
  }

  switch ((lastTool as any).toolName) {
    case AIChatToolIds.QUERY_CONVERSATIONAL_ANALYTICS:
      return AIChatStatuses.ANALYZING_QUESTION;
    case AIChatToolTypes.ASSET_DRAFT_FROM_PHOTO:
      return AIChatStatuses.EXTRACTING_PHOTO;
    default:
      return AIChatStatuses.THINKING;
  }
};

export const useChatStatus = (): AIChatStatuses | null => {
  const message = useAssistantState(state => state.message);

  return (
    getWorkflowStatus(message.content) ??
    getAnalyticsStatus(message.content) ??
    getToolCallStatus(message.content)
  );
};
